import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Nav from '../components/Nav';

import Footer from '../components/Footer';
import { getProjects } from '../data/projects';

export default function Projet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [idx, setIdx] = useState(-1);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  useEffect(() => {
    const all = getProjects();
    const i = all.findIndex(p => p.id === parseInt(id));
    setProjects(all);
    setIdx(i);
    if (i >= 0) {
      setProject(all[i]);
      document.title = all[i].name + ' — HEYIMPURP';
    }
  }, [id]);

  // Lightbox keyboard nav
  const validImgs = project ? (project.images || []).map((img, i) => img ? i : -1).filter(i => i >= 0) : [];

  const lbNav = useCallback((dir) => {
    setLightbox(lb => {
      if (!validImgs.length) return lb;
      const pos = validImgs.indexOf(lb.index);
      const next = validImgs[(pos + dir + validImgs.length) % validImgs.length];
      return { ...lb, index: next };
    });
  }, [validImgs]);

  useEffect(() => {
    const handler = (e) => {
      if (!lightbox.open) return;
      if (e.key === 'ArrowRight') lbNav(1);
      if (e.key === 'ArrowLeft')  lbNav(-1);
      if (e.key === 'Escape')     setLightbox(lb => ({ ...lb, open: false }));
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightbox.open, lbNav]);

  useEffect(() => {
    document.body.style.overflow = lightbox.open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [lightbox.open]);

  if (!project) {
    return (
      <>
        <Nav />
        <div style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, textAlign: 'center', padding: 40 }}>
          <h2 style={{ fontFamily: "'Barlow Condensed'", fontSize: '3rem', fontWeight: 900, textTransform: 'uppercase' }}>Projet introuvable</h2>
          <p style={{ color: 'var(--muted)' }}>Ce projet n'existe pas ou a été supprimé.</p>
          <Link to="/portfolio" style={{ color: 'var(--red)', fontFamily: "'Barlow Condensed'", fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', textDecoration: 'none', borderBottom: '1px solid var(--red)', paddingBottom: 2 }}>← Retour aux projets</Link>
        </div>
        <Footer />
      </>
    );
  }

  const prev = projects[idx - 1];
  const next = projects[idx + 1];
  const realImgs = (project.images || []).filter(Boolean);

  return (
    <>
      <Nav />
      {/* BREADCRUMB */}
      <div className="breadcrumb">
        <Link to="/portfolio">← Projets</Link>
        <span className="sep">/</span>
        <span>{project.name}</span>
      </div>

      {/* HERO */}
      <div className="project-hero">
        <div>
          <div className="project-cat-tag">{project.catLabel}</div>
          <h1 className="project-title">{project.name}</h1>
          <p className="project-desc-detail">{project.desc}</p>
          <div className="project-tags">
            {(project.tools || []).map(t => <span className="tag" key={t}>{t}</span>)}
          </div>
        </div>
        <div className="project-meta">
          <div className="meta-item">
            <div className="meta-label">Client</div>
            <div className="meta-value">{project.client || '—'}</div>
          </div>
          <div className="meta-item">
            <div className="meta-label">Date de réalisation</div>
            <div className="meta-value">{project.date || '—'}</div>
          </div>
          <div className="meta-item">
            <div className="meta-label">Catégorie</div>
            <div className="meta-value">{project.catLabel || '—'}</div>
          </div>
          <div className="meta-item">
            <div className="meta-label">Rôle / Mission</div>
            <div className="meta-value">{project.role || '—'}</div>
          </div>
          <div className="meta-item">
            <div className="meta-label">Outils utilisés</div>
            <div className="meta-value">
              <div className="meta-tools">
                {(project.tools || []).map(t => <span className="meta-tool" key={t}>{t}</span>)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* HERO IMAGE */}
      <div className="hero-image-wrap">
        {project.heroImage
          ? <img className="hero-image" src={project.heroImage} alt={project.name} />
          : (
            <div className="hero-placeholder" style={{ background: project.heroBg || '#111' }}>
              {project.heroLetter || project.name[0]}
            </div>
          )
        }
      </div>

      {/* CONTENT */}
      <div className="project-content">
        <div>
          <div className="content-label">Contexte & Mission</div>
          <h2 className="content-title">LE PROJET</h2>
          <div className="content-body">
            <p>{project.context}</p>
            <p>{project.approach}</p>
          </div>
        </div>
        <div className="content-aside">
          <div className="aside-block">
            <div className="aside-title">Résultats</div>
            <div className="aside-text">{project.result || '—'}</div>
          </div>
          <div className="aside-block">
            <div className="aside-title">Projet similaire ?</div>
            <div className="aside-text" style={{ marginBottom: 12 }}>Vous avez un besoin dans cette catégorie ? Échangeons.</div>
            <Link to="/contact" className="aside-cta">Me contacter →</Link>
          </div>
        </div>
      </div>

      {/* GALLERY */}
      <div className="gallery-section">
        <div className="gallery-header">
          <h2 className="gallery-title">GALERIE</h2>
          {realImgs.length > 0 && (
            <span className="gallery-count">{realImgs.length} visuel{realImgs.length > 1 ? 's' : ''}</span>
          )}
        </div>
        <div className="gallery-grid">
          {(project.images || ['', '', '', '']).map((img, gi) => {
            const bg = (project.imageBgs || [])[gi] || '#181818';
            return (
              <div
                key={gi}
                className="gallery-item"
                onClick={() => img && setLightbox({ open: true, index: gi })}
              >
                {img
                  ? <img className="gallery-img" src={img} alt={`${project.name} ${gi + 1}`} />
                  : <div className="gallery-placeholder" style={{ background: bg }}>{project.heroLetter || project.name[0]}</div>
                }
                {img && <div className="gallery-zoom">⤢</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* PREV / NEXT */}
      <div className="project-nav">
        {prev
          ? <Link to={`/projet/${prev.id}`} className="proj-nav-item">
              <span className="proj-nav-dir">← Projet précédent</span>
              <span className="proj-nav-name">{prev.name}</span>
              <span className="proj-nav-cat">{prev.catLabel}</span>
            </Link>
          : <div className="proj-nav-item" style={{ opacity: 0.2, pointerEvents: 'none' }}>
              <span className="proj-nav-dir">← Projet précédent</span>
              <span className="proj-nav-name">—</span>
            </div>
        }
        {next
          ? <Link to={`/projet/${next.id}`} className="proj-nav-item" style={{ textAlign: 'right' }}>
              <span className="proj-nav-dir">Projet suivant →</span>
              <span className="proj-nav-name">{next.name}</span>
              <span className="proj-nav-cat">{next.catLabel}</span>
            </Link>
          : <div className="proj-nav-item" style={{ opacity: 0.2, pointerEvents: 'none', textAlign: 'right' }}>
              <span className="proj-nav-dir">Projet suivant →</span>
              <span className="proj-nav-name">—</span>
            </div>
        }
      </div>

      <Footer />

      {/* LIGHTBOX */}
      {lightbox.open && (
        <div
          className="lightbox open"
          onClick={(e) => e.target.classList.contains('lightbox') && setLightbox(lb => ({ ...lb, open: false }))}
        >
          <button className="lightbox-close" onClick={() => setLightbox(lb => ({ ...lb, open: false }))}>✕ Fermer</button>
          <button className="lightbox-nav lightbox-prev" onClick={() => lbNav(-1)}>←</button>
          <img className="lightbox-img" src={project.images[lightbox.index]} alt="" />
          <button className="lightbox-nav lightbox-next" onClick={() => lbNav(1)}>→</button>
          <div className="lightbox-counter">
            {validImgs.indexOf(lightbox.index) + 1} / {validImgs.length}
          </div>
        </div>
      )}
    </>
  );
}
